import {
  ApplyImportResponseSchema,
  type CreateUpload,
  ImportChecksResponseSchema,
  ImportResponseSchema,
  ImportRowResponseSchema,
  ImportRowsResponseSchema,
  type ResolveProblem,
  UploadResponseSchema,
  type UploadTicket,
  UploadTicketSchema,
  UploadViewSchema,
} from '@oneapp/shared';
import { ApiError, api } from '../lib/api';

const withSignal = (signal?: AbortSignal) => (signal ? { signal } : {});
const enc = encodeURIComponent;

/** Sales files (Phase 7; see packages/shared/src/api.ts), as the signed-in person. */
export const importsApi = {
  /** A link to put the file straight into storage. */
  createUpload: (input: CreateUpload) => api.post('/api/v1/uploads', UploadTicketSchema, { auth: true, body: input }),

  /** Puts the file where the link says: exactly its size and type, before the link expires. */
  async put(ticket: UploadTicket, file: Blob, signal?: AbortSignal): Promise<void> {
    let response: Response;
    try {
      response = await fetch(ticket.url, { method: 'PUT', headers: ticket.headers, body: file, ...withSignal(signal) });
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') throw error;
      throw new ApiError('The file couldn’t be uploaded', 0);
    }
    if (!response.ok) throw new ApiError('The file couldn’t be uploaded', response.status);
  },

  /** The file is in: it's checked (size, what it really is, malware) before anything reads it. */
  complete: (uploadId: string) =>
    api.post(`/api/v1/uploads/${enc(uploadId)}/complete`, UploadResponseSchema, { auth: true }),

  get: (importId: string, signal?: AbortSignal) =>
    api.get(`/api/v1/imports/${enc(importId)}`, ImportResponseSchema, { auth: true, ...withSignal(signal) }),

  review: (importId: string, after = 0) =>
    api.get(`/api/v1/imports/${enc(importId)}/rows?after=${after}`, ImportRowsResponseSchema, { auth: true }),
  apply: (importId: string) =>
    api.post(`/api/v1/imports/${enc(importId)}/apply`, ApplyImportResponseSchema, { auth: true }),
  row: (importId: string, rowId: string, signal?: AbortSignal) =>
    api.get(`/api/v1/imports/${enc(importId)}/rows/${enc(rowId)}`, ImportRowResponseSchema, {
      auth: true,
      ...withSignal(signal),
    }),

  resolve: (importId: string, problemId: string, body: ResolveProblem) =>
    api.post(`/api/v1/imports/${enc(importId)}/problems/${enc(problemId)}/resolve`, ImportResponseSchema, {
      auth: true,
      body,
    }),

  /** Photos: the invoices with fields to check (Phase 8). */
  checks: (importId: string, signal?: AbortSignal) =>
    api.get(`/api/v1/imports/${enc(importId)}/checks`, ImportChecksResponseSchema, {
      auth: true,
      ...withSignal(signal),
    }),

  /** Photos: what a field really is (the value read, or the right one). */
  /** `tellChat: false` from the chat's own sheet, which tells the chat itself when it closes. */
  confirm: (importId: string, checkId: string, value: string, options: { tellChat?: boolean } = {}) =>
    api.post(`/api/v1/imports/${enc(importId)}/checks/${enc(checkId)}/confirm`, ImportResponseSchema, {
      auth: true,
      body: { value, ...(options.tellChat === false ? { tellChat: false } : {}) },
    }),

  /** A link to see one of the person's photos, for a few minutes. */
  view: (uploadId: string, signal?: AbortSignal) =>
    api.get(`/api/v1/uploads/${enc(uploadId)}/view`, UploadViewSchema, { auth: true, ...withSignal(signal) }),
};

export const importKeys = {
  import: (importId: string) => ['import', importId] as const,
  review: (importId: string, after = 0) => ['import', importId, 'review', after] as const,
  row: (importId: string, rowId: string) => ['import', importId, 'row', rowId] as const,
  checks: (importId: string) => ['import', importId, 'checks'] as const,
  view: (uploadId: string) => ['upload', uploadId, 'view'] as const,
};

/** The template, as a link anyone can download (no sign-in needed). */
export const templateUrl = (format: 'xlsx' | 'csv') => `/api/v1/templates/sales-register?format=${format}`;

/** "/imports/{id}/rows/{row}?problem={problem}": the page to fix one row of a file. */
export function parseImportPath(path: string): { importId: string; rowId: string; problemId: string | null } | null {
  const m = /^\/imports\/([\w-]{1,64})\/rows\/([\w-]{1,64})\/?(?:\?problem=([\w-]{1,64}))?$/.exec(path);
  if (!m) return null;
  return { importId: m[1] as string, rowId: m[2] as string, problemId: m[3] ?? null };
}

/** "/imports/{id}/checks": the page to check photos' unclear fields. */
export function parseChecksPath(path: string): { importId: string } | null {
  const m = /^\/imports\/([\w-]{1,64})\/checks\/?$/.exec(path);
  return m ? { importId: m[1] as string } : null;
}

/** "CSV · 2 KB", as the file's message and the upload sheet show it. */
export function fileLabel(name: string, size: number): string {
  const ending = /\.(\w+)$/.exec(name)?.[1]?.toUpperCase() ?? 'FILE';
  return `${ending} · ${sizeLabel(size)}`;
}

/** "2 KB", "2.1 MB". */
export function sizeLabel(size: number): string {
  const kb = Math.max(1, Math.round(size / 1024));
  return kb >= 1024 ? `${(size / (1024 * 1024)).toFixed(1)} MB` : `${kb} KB`;
}
